import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Agent Target master (Masters → Agent Targets). A sales target assigned to a
 * user for a period (monthly / quarterly / annual). Unlike the lookup masters
 * this is relational data: it has NO `code` / `name` / `isActive` /
 * `sortOrder` / `legacyId`. Targets are **effective-dated**: exactly one
 * active row per agent (`periodEnd = NULL`); updating an agent's target
 * closes the current active row and inserts a new one, preserving full
 * history. Soft-deleted via `deletedAt`. See ADR-0016 / ADR-0027.
 */

/** Mirrors the Prisma `TargetPeriod` enum. */
export const TargetPeriodSchema = z.enum(['MONTHLY', 'QUARTERLY', 'ANNUAL']);
export type TargetPeriod = z.infer<typeof TargetPeriodSchema>;

/**
 * Mirrors the Prisma `TargetBasis` enum. TURNOVER = gross billed to
 * customer; REVENUE = profit only (gross minus supplier net cost). See
 * ADR-0043.
 */
export const TargetBasisSchema = z.enum(['TURNOVER', 'REVENUE']);
export type TargetBasis = z.infer<typeof TargetBasisSchema>;

/** Calendar date (no time component), `YYYY-MM-DD` - maps to a Postgres `date`. */
export const DateOnlySchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

/** Slim user reference embedded in list/detail rows. */
const UserRefSchema = z.object({ id: UuidSchema, fullName: z.string() });

/**
 * Slim destination reference. Just the id + name so the list page can
 * render a chip without a second fetch; full destination geometry
 * (region / country / state / city) is left to the destinations
 * list endpoint when the operator drills in. See ADR-0044.
 */
const DestinationRefSchema = z.object({ id: UuidSchema, name: z.string() });

export const AgentTargetListItemSchema = z.object({
  id: UuidSchema,
  user: UserRefSchema,
  period: TargetPeriodSchema,
  /** Effective-dating start boundary - the first day of the month the
   *  target became active. Set entirely by the backend; the client never
   *  sends it. */
  periodStart: DateOnlySchema,
  /** Effective-dating end boundary. `NULL` = this is the **active**
   *  target (exactly one active row per agent). A non-null value means
   *  the row is historical: it was superseded on this date. See the
   *  effective-dating flow in `AgentTargetsService`. */
  periodEnd: DateOnlySchema.nullable(),
  targetAmount: z.number(),
  /** TURNOVER vs REVENUE basis. Marked `.nullable().optional()` so a
   *  server response that hasn't been upgraded yet (old API rolling
   *  behind the new migration, or the new API running before the
   *  migration has been applied to the DB) still parses cleanly. The
   *  UI defaults to TURNOVER for legacy rows where the field is
   *  absent. */
  targetBasis: TargetBasisSchema.nullable().optional(),
  /** Optional destination scope. NULL = "no destination, overall quota
   *  for the period"; non-NULL scopes the quota to one destination.
   *  Marked `.nullable().optional()` so a response from a server that
   *  hasn't been upgraded yet (or that's running pre-migration) parses
   *  cleanly. See ADR-0044. */
  destination: DestinationRefSchema.nullable().optional(),
  /** Loose FK to `currencies` (no Prisma relation); resolve label client-side. */
  currencyId: UuidSchema.nullable(),
  targetLeads: z.number().int().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AgentTargetListItem = z.infer<typeof AgentTargetListItemSchema>;

export const AgentTargetListQuerySchema = PaginationQuerySchema.extend({
  userId: UuidSchema.optional(),
  period: TargetPeriodSchema.optional(),
  targetBasis: TargetBasisSchema.optional(),
  /** Filter by destination. Use the sentinel string `none` to filter on
   *  NULL (the overall-per-period rows). Any other value is parsed as
   *  a destination UUID. */
  destinationId: z.union([UuidSchema, z.literal('none')]).optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
  /** Include superseded (historical) rows. By default the list returns only
   *  each agent's active target (`periodEnd IS NULL`); set this (with a
   *  `userId`) to get the full target timeline for the per-agent history popup. */
  includeHistory: BooleanQueryParamSchema.optional(),
});
export type AgentTargetListQuery = z.infer<typeof AgentTargetListQuerySchema>;

export const AgentTargetListResponseSchema = paginatedResponseSchema(AgentTargetListItemSchema);
export type AgentTargetListResponse = z.infer<typeof AgentTargetListResponseSchema>;

export const AgentTargetDetailSchema = AgentTargetListItemSchema.extend({
  notes: z.string().nullable(),
  setBy: UserRefSchema.nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type AgentTargetDetail = z.infer<typeof AgentTargetDetailSchema>;

const AgentTargetCoreFieldsSchema = z.object({
  userId: UuidSchema.describe('Agent (user) the target is for. Use GET /users.'),
  period: TargetPeriodSchema,
  // Bounded to the DB column `Decimal(14,2)` (12 integer digits + 2 decimals)
  // so an absurdly large input fails validation with a clear message instead
  // of a numeric-overflow 500 on insert.
  targetAmount: z
    .number()
    .min(0, 'Target amount must be zero or more')
    .max(999_999_999_999.99, 'Target amount is too large (max 999,999,999,999.99)'),
  /** Defaults to REVENUE on the server when omitted - that's the
   *  primary metric the operations team tracks targets against
   *  (post ADR-0043 update). TURNOVER remains a valid pick for
   *  rows where the admin wants gross-billed targets. */
  targetBasis: TargetBasisSchema.optional(),
  /** Legacy lead-count column. The form no longer surfaces it; kept
   *  optional so an edit round-trip can preserve a historical value. */
  targetLeads: z.number().int().min(0).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});

/**
 * Create + Update share the same field set (Update is NOT a `.partial()` -
 * the form submits the full editable set). Effective dating is entirely
 * backend-owned: the client never sends `periodStart` / `periodEnd`
 * (the backend derives them), `destinationId` (dropped from the model),
 * or `currencyId` (hardcoded server-side). `setById` is set from the actor.
 */
export const CreateAgentTargetRequestSchema = AgentTargetCoreFieldsSchema;
export type CreateAgentTargetRequest = z.infer<typeof CreateAgentTargetRequestSchema>;

export const UpdateAgentTargetRequestSchema = AgentTargetCoreFieldsSchema;
export type UpdateAgentTargetRequest = z.infer<typeof UpdateAgentTargetRequestSchema>;

/**
 * Stats payload for the Lead Inbox business strip. Returned by the
 * server already RBAC-scoped: SALES_AGENT gets `my` filled and `team`
 * = null; SALES_LEAD gets both filled (my = self, team = direct
 * reports' aggregate); unscoped roles (ADMIN / OPS / FINANCE /
 * READ_ONLY) get `team` filled with the org-wide aggregate and `my`
 * = null when the actor doesn't carry personal targets. The client
 * decides layout from which slot is populated.
 *
 * `basis` is echoed back so the UI can label tiles correctly when an
 * operator switches between "Turnover" and "Revenue" views.
 *
 * Achievement aggregation today: sum of `Lead.budgetAmount` for WON
 * leads in scope and inside the period window. Phase 6 Bookings will
 * upgrade revenue-basis achievement to actual booking profit; the
 * payload shape stays identical so the UI doesn't need to change.
 */
export const AgentTargetStatsBucketSchema = z.object({
  targetAmount: z.number().nonnegative(),
  achievementAmount: z.number().nonnegative(),
  wonLeadCount: z.number().int().nonnegative(),
  shortAmount: z.number(),
});
export type AgentTargetStatsBucket = z.infer<typeof AgentTargetStatsBucketSchema>;

export const AgentTargetStatsQuerySchema = z.object({
  period: TargetPeriodSchema.default('MONTHLY'),
  periodStart: DateOnlySchema.optional(),
  basis: TargetBasisSchema.default('TURNOVER'),
  /**
   * 2026-07-13. Optional owner filter. When set, both `my` and `team`
   * are recomputed AS IF the target user was the caller: `my` is the
   * target user's personal bucket; `team` is the target user's team
   * (their reports if they're a SALES_LEAD, else null). The server
   * validates that the caller has visibility on this user - a
   * SALES_LEAD can only pick themselves or a direct report; ADMIN /
   * OPS / FINANCE / READ_ONLY can pick any active user. Omitting
   * `userId` keeps the pre-2026-07-13 behaviour (stats scoped to the
   * caller's own tree). See LeadsBusinessStrip for the UI wiring.
   */
  userId: UuidSchema.optional(),
  /**
   * 2026-07-21. Optional conversion-date window for the "Files Converted
   * (Won)" count ONLY. When both are set, `wonLeadCount` counts leads whose
   * `closedAt` (conversion instant) falls in `[wonFrom, wonTo]` (interpreted
   * as IST day bounds), instead of the target period. This lets the inbox's
   * date-range filter drive the converted-count while `targetAmount` /
   * `achievementAmount` / `shortAmount` stay pinned to the monthly quota
   * period (they are quota concepts - see ADR-0043). Omit both to keep the
   * pre-2026-07-21 behaviour where the count also uses the period window.
   * Ignored unless BOTH are present.
   */
  wonFrom: DateOnlySchema.optional(),
  wonTo: DateOnlySchema.optional(),
});
export type AgentTargetStatsQuery = z.infer<typeof AgentTargetStatsQuerySchema>;

export const AgentTargetStatsResponseSchema = z.object({
  basis: TargetBasisSchema,
  period: TargetPeriodSchema,
  periodStart: DateOnlySchema,
  periodEnd: DateOnlySchema,
  my: AgentTargetStatsBucketSchema.nullable(),
  team: AgentTargetStatsBucketSchema.nullable(),
});
export type AgentTargetStatsResponse = z.infer<typeof AgentTargetStatsResponseSchema>;
